import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { formatDate } from "../lib/date";
import { useStockItemDetail } from "../hooks/use-stock-items";
import styles from "./StockItemDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/stock-items/$id",
	component: withPermission(StockItemDetailPage, "stock_items", "read"),
});

function StockItemDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const itemQuery = useStockItemDetail(id);

	if (itemQuery.isLoading) return <LoadingSpinner />;
	if (itemQuery.isError || !itemQuery.data) return <ErrorFallback onRetry={itemQuery.refetch} />;

	const item = itemQuery.data;

	return (
		<div>
			<BackButton fallbackTo="/stock-items" />
			<PageHeader title={item.name} />

			<Card header={t("stockItemInfo")}>
				<div className={styles.infoGrid}>
					<Info label={t("code")} value={item.code} />
					<Info label={t("name")} value={item.name} />
					<Info label={t("description")} value={item.description} />
					<Info label={t("category")} value={item.category} />
					<Info label={t("uom")} value={item.uom} />
					<Info label={t("sapItemCode")} value={item.sapItemCode} />
					<Info
						label={t("sapItemGroup")}
						value={item.sapItemGroup ? String(item.sapItemGroup) : null}
					/>
					<Info label={t("sapSalesUnit")} value={item.sapSalesUnit} />
					<Info label={t("sapPurchaseUnit")} value={item.sapPurchaseUnit} />
					<Info
						label={t("currentStock")}
						value={item.currentStock != null ? String(item.currentStock) : "-"}
					/>
					<Info
						label={t("reservedAmount")}
						value={item.reservedAmount != null ? String(item.reservedAmount) : "-"}
					/>
					<Info
						label={t("onOrderAmount")}
						value={item.onOrderAmount != null ? String(item.onOrderAmount) : "-"}
					/>
					<Info label={t("minStock")} value={item.minStock != null ? String(item.minStock) : "-"} />
					<Info label={t("maxStock")} value={item.maxStock != null ? String(item.maxStock) : "-"} />
					<Info
						label={t("reorderPoint")}
						value={item.reorderPoint != null ? String(item.reorderPoint) : "-"}
					/>
					<Info
						label={t("lastSyncedAt")}
						value={item.lastSyncedAt ? formatDate(item.lastSyncedAt) : "-"}
					/>
				</div>
			</Card>
		</div>
	);
}

function Info({ label, value }: { label: string; value: string | null }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
